import { Badge } from '@/components/ui'
import { PROMPT_EXAMPLES, type PromptExample } from '@/lib/promptHints'

/**
 * PromptExamples (#1494) — a small "here's what a good prompt looks like" block
 * for the discovery dialog. Shows 2-3 worked examples (happy path / negative /
 * edge), each with the prompt sentence AND the flow it would cover, so a
 * first-timer learns both WHAT to write and the level of detail. "Use this"
 * drops the prompt sentence into the box (the caller decides set-vs-append).
 *
 * Replaces the single one-liner example (#1486) — users said one hint wasn't
 * enough to understand what a good prompt is. Rendered only when the prompt is
 * empty (the dialog gates it), so it disappears once the user starts typing.
 */

const KIND_BADGE: Record<PromptExample['kind'], string> = {
  happy: 'text-green-700 dark:text-green-400 border-green-300 dark:border-green-800',
  negative: 'text-red-700 dark:text-red-400 border-red-300 dark:border-red-800',
  edge: 'text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-800',
}

export function PromptExamples({ onUse }: { onUse: (prompt: string) => void }) {
  return (
    <div className="rounded border border-border bg-muted/30 text-xs p-2.5">
      <div className="font-medium">
        Not sure what to write? Here are good examples — tap “Use this” to start, then edit.
      </div>
      {/* The principle the examples model — a vague prompt with no starting
          point can't be planned, so spell out what makes a prompt good. */}
      <div className="mt-1 mb-2 text-muted-foreground">
        A good prompt names <span className="text-foreground font-medium">where to start</span>,{' '}
        <span className="text-foreground font-medium">what to do</span>, and{' '}
        <span className="text-foreground font-medium">what should pass</span>.
      </div>
      {PROMPT_EXAMPLES.map(ex => (
        <div key={`${ex.kind}-${ex.prompt}`} className="border-t border-border pt-2 mt-2 first:border-t-0 first:pt-0 first:mt-0">
          <div className="flex items-start gap-2">
            <Badge variant="outline" className={`shrink-0 text-[10px] ${KIND_BADGE[ex.kind] ?? ''}`}>
              {ex.label}
            </Badge>
            <span className="font-medium min-w-0">{ex.prompt}</span>
            <button
              type="button"
              // Don't steal focus from the textarea (matches the Clear
              // affordance) so a keyboard user keeps typing.
              onMouseDown={e => e.preventDefault()}
              onClick={() => onUse(ex.prompt)}
              className="ml-auto shrink-0 font-medium text-primary hover:underline"
            >
              Use this
            </button>
          </div>
          <div className="mt-1 text-muted-foreground">Steps: {ex.steps.join(' → ')}</div>
        </div>
      ))}
    </div>
  )
}
